import { Injectable, signal, inject, computed } from '@angular/core';
import { CustomData, LandingpageConfiguration } from 'app/shared/models/landingpage-configuration.model';
import { Organisation } from 'app/shared/models/organisation.model';
import { CardInformation } from 'app/shared/models/dzbank-configuration.model';
import { Customer } from 'app/shared/models/customer.model';
import { cardProductTexts } from 'app/shared/models/card-product-texts';
import { lastValueFrom } from 'rxjs';
import { AuthenticationStateService } from './authentication-state.service';
import { BackendService } from '../backend.service';
import { LandingpageOrganisationResponseDto } from '../../models/dto/landingpage-organisation-response-dto';

@Injectable({
  providedIn: 'root',
})
export class LandingpageConfigurationStateService {
  private authenticationStateService = inject(AuthenticationStateService);
  private backendService = inject(BackendService);

  private organisationSignal = signal<Organisation | null>(null);
  private assetStackBucketUrlSignal = signal<string | null>(null);
  private cardInformationSignal = signal<CardInformation | null>(null);
  private customerSignal = signal<Customer | null>(null);
  private customDataSignal = signal<CustomData | null>(null);
  private ibanAnonymisedSignal = signal<string | null>(null);

  readonly organisation = this.organisationSignal.asReadonly();
  readonly assetStackBucketUrl = this.assetStackBucketUrlSignal.asReadonly();
  readonly cardInformation = this.cardInformationSignal.asReadonly();
  readonly customer = this.customerSignal.asReadonly();
  readonly customData = this.customDataSignal.asReadonly();
  readonly ibanAnonymised = this.ibanAnonymisedSignal.asReadonly();
  readonly cardProductTexts = computed(() => {
    const cardInformation = this.cardInformation();
    return cardInformation ? cardProductTexts[cardInformation.cardProduct] : undefined;
  });

  setLandingpageOrganisation(value: LandingpageOrganisationResponseDto) {
    this.assetStackBucketUrlSignal.set(value.assetStackBucketUrl);
    this.organisationSignal.set(value.organisation);
  }

  setLandingpageConfiguration(value: LandingpageConfiguration) {
    this.assetStackBucketUrlSignal.set(value.assetStackBucketUrl);
    this.organisationSignal.set(value.organisation);
    this.cardInformationSignal.set(value.cardInformation);
    this.customerSignal.set(value.customer);
    this.customDataSignal.set(value.customData);
    this.ibanAnonymisedSignal.set(value.ibanAnonymised);
  }

  setCustomerEmail(email: string) {
    this.customerSignal.update((customer) => {
      if (customer) customer.email = email;
      return customer;
    });
  }

  hasOrganisation(): boolean {
    return !!this.organisationSignal();
  }

  hasConfiguration(): boolean {
    return (
      !!this.cardInformationSignal() &&
      !!this.customerSignal() &&
      !!this.customDataSignal() &&
      !!this.ibanAnonymisedSignal()
    );
  }

  async fetchOrganisationForToken(token: string): Promise<void> {
    const response = await lastValueFrom(this.backendService.submitPersonalAccessToken(token));
    this.authenticationStateService.setPersonalAccessToken(token);
    this.setLandingpageOrganisation(response);
  }

  async fetchPublicOrganisationForToken(token: string): Promise<void> {
    const response = await lastValueFrom(this.backendService.getPublicLandingpageOrganisation(token));
    this.setLandingpageOrganisation(response);
  }

  async fetchConfigurationForPassword(password: string): Promise<void> {
    const response = await lastValueFrom(this.backendService.submitAuthenticationToken(password));
    this.authenticationStateService.setPassword(password);
    this.setLandingpageConfiguration(response);
  }

  async fetchOrganisation(): Promise<void> {
    await this.fetchOrganisationForToken(this.authenticationStateService.personalAccessToken()!);
  }

  async fetchConfiguration(): Promise<void> {
    await this.fetchConfigurationForPassword(this.authenticationStateService.password()!);
  }
}
